/**
 * Client branding configuration.
 *
 * Default values are sourced from brandingDefaults.ts.
 * In production, these values can be overridden by config/branding.json
 * which is loaded at runtime for white-label deployments.
 */

import { BRANDING } from '@/config/brandingDefaults'

export interface BrandingConfig {
  // Application info
  appName: string
  appShortName: string
  tagline: string

  // Logo paths
  logoLight: string
  logoDark: string
  logoIcon: string
  favicon: string

  // Colors (HSL values for CSS variables)
  colors: {
    primary: string
    primaryForeground: string
    secondary: string
    secondaryForeground: string
    accent: string
    accentForeground: string
  }

  // Contact info
  supportEmail: string
  supportUrl: string

  // Feature flags
  features: {
    showPoweredBy: boolean
    customFooter: string | null
  }
}

// Default branding (identity and contact from brandingDefaults.ts)
export const defaultBranding: BrandingConfig = {
  appName: BRANDING.appName,
  appShortName: BRANDING.appShortName,
  tagline: BRANDING.tagline,

  // Default placeholder logos
  logoLight: '/logo-light.svg',
  logoDark: '/logo-dark.svg',
  logoIcon: '/logo-icon.svg',
  favicon: '/favicon.ico',

  // Default colors (HSL values for CSS variables)
  colors: {
    primary: '221.2 83.2% 53.3%',
    primaryForeground: '210 40% 98%',
    secondary: '210 40% 96.1%',
    secondaryForeground: '222.2 47.4% 11.2%',
    accent: '210 40% 96.1%',
    accentForeground: '222.2 47.4% 11.2%',
  },

  supportEmail: BRANDING.footer.supportEmail,
  supportUrl: BRANDING.footer.supportUrl,

  features: {
    showPoweredBy: BRANDING.footer.showPoweredBy,
    customFooter: null,
  },
}

// Branding state (can be updated at runtime)
let currentBranding: BrandingConfig = { ...defaultBranding }

/**
 * Get the current branding configuration.
 */
export function getBranding(): BrandingConfig {
  return currentBranding
}

/**
 * Update branding configuration.
 * Used when loading client-specific branding from server.
 */
export function setBranding(branding: Partial<BrandingConfig>): void {
  currentBranding = {
    ...defaultBranding,
    ...branding,
    colors: {
      ...defaultBranding.colors,
      ...branding.colors,
    },
    features: {
      ...defaultBranding.features,
      ...branding.features,
    },
  }

  // Apply colors to CSS variables
  applyBrandColors(currentBranding.colors)
}

/**
 * Apply brand colors to CSS custom properties.
 */
function applyBrandColors(colors: BrandingConfig['colors']): void {
  const root = document.documentElement

  Object.entries(colors).forEach(([key, value]) => {
    // Convert camelCase to kebab-case
    const cssVar = key.replace(/([A-Z])/g, '-$1').toLowerCase()
    root.style.setProperty(`--${cssVar}`, value)
  })
}

/**
 * Load branding from server config file.
 * Returns true if custom branding was loaded.
 */
export async function loadBrandingFromServer(): Promise<boolean> {
  try {
    const response = await fetch('/config/branding.json')
    if (!response.ok) {
      console.log('No custom branding found, using defaults')
      return false
    }

    const customBranding = await response.json()
    setBranding(customBranding)
    console.log('Custom branding loaded:', customBranding.appName)
    return true
  } catch (error) {
    console.log('Failed to load custom branding, using defaults:', error)
    return false
  }
}
